import { FC, useMemo } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { Snackbar } from '@robocorp/components';
import ViewLoader from '~/components/ViewLoader';
import { TimeRangeProvider } from '~/components/time/timeRange/Provider';
import ErrorBoundary from '~/components/ErrorBoundary';
import { OrganizationInviteCapture } from '~/components/OrganizationInviteCapture';
import AuthSessionGuard from '~/components/AuthSessionGuard';
import { ToSGuard } from '~/components/ToSGuard';
import VersionChecker from '~/components/VersionChecker';
import SlackInviter from '~/components/SlackInviter';
import FeedbackDialog from '~/components/FeedbackDialog';
import UserMessageHandler from '~/components/UserMessageHandler';
import ServiceDeskDialog from '~/components/ServiceDeskDialog';
import OrganizationActivityChecker from '~/components/OrganizationActivityChecker';
import WorkspaceActivityChecker from '~/components/WorkspaceActivityChecker';
import WorkspaceSocket from '~/components/sockets/WorkspaceSocket';
import { AuthProvider, useAuth, AuthClientOpts } from '~/auth/AuthProvider';
import { Title } from '~/routes/app/Title';
import { ConfirmProvider, LocalStorageItemKeys } from '~/hooks';
import { withGlobalUserSettings, withUser, withOrganization, withWorkspace } from '~/hocs';
import { useSlackCallback } from '~/hooks/useSlackCallback';
import { AppLayout } from '~/components/layout/App';
import { Banners } from '~/components/integrations';
import CreateOrganizationDialog from '~/routes/user/create-organization/components/CreateOrganizationDialog';
import CreateWorkspaceDialog from '~/routes/user/create-workspace/components/CreateWorkspaceForOrgDialog';
import { TimeRangeValues } from '~/constants/timeRanges';
import { store as reduxStore } from './store';

const authOptions: AuthClientOpts = {
  type: import.meta.env.VITE_AUTHORIZATION_PROVIDER_TYPE as AuthClientOpts['type'],
  redirectUri: `${window.location.origin}/signin-callback`,
};

/**
 * 1. Router conscious setup/providers.
 */
export const RootLayer: FC = () => (
  <ReduxProvider store={reduxStore}>
    <AuthProvider authOptions={authOptions}>
      <OrganizationInviteCapture>
        <Title />
        <Outlet />
        <ScrollRestoration />
      </OrganizationInviteCapture>
    </AuthProvider>
  </ReduxProvider>
);

/**
 * 2. Must be authenticated to pass this route layer
 */
export const ProtectedLayer: FC = () => {
  const { isAuthenticated, loading, client } = useAuth();

  // TODO refresh authentication?
  // if so we can ask to renew the token in an effect

  if (loading) {
    return <ViewLoader key="initial" initial />;
  }

  if (!isAuthenticated) {
    const pathname = window.location.pathname || import.meta.env.VITE_USER_FRONTPAGE;
    const returnToUrl = `${window.location.protocol}//${window.location.host}${pathname}`;
    client.redirectToLogin(returnToUrl);

    return <ViewLoader key="initial" initial />;
  }
  return <Outlet />;
};

/**
 * 3. Allow to access most basic routes
 */
export const HomeLayer: FC = withUser(
  withGlobalUserSettings(() => (
    <>
      <AuthSessionGuard>
        <Snackbar max={5}>
          <ConfirmProvider>
            <ToSGuard>
              <UserMessageHandler />
              <VersionChecker />
              <SlackInviter />
              <FeedbackDialog />
              <ServiceDeskDialog />
              <Outlet />
            </ToSGuard>
          </ConfirmProvider>
        </Snackbar>
      </AuthSessionGuard>
    </>
  )),
);

/**
 * 4. Allow to access user routes
 */
export const UserLayer: FC = () => (
  <>
    <CreateWorkspaceDialog />
    <CreateOrganizationDialog />
    {/* 
    Disable releasenotes.io temporarly as the API calls to releasenotes.io are broken
      <ReleaseNotes />
    */}
    <Banners />
    <AppLayout>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AppLayout>
  </>
);

/**
 * 5. Allow to access organization routes
 */
export const OrganizationLayer: FC = withOrganization(({ currentOrganization }) => {
  const permissions = currentOrganization?.permissions;
  const canManageOrganizationBilling = useMemo(
    () => permissions?.includes('manage_billing') ?? false,
    [permissions],
  );

  return (
    <TimeRangeProvider.Persisted
      preset={
        canManageOrganizationBilling
          ? TimeRangeValues.CURRENT_BILLING_PERIOD
          : TimeRangeValues.THIS_MONTH
      }
      storageKey={LocalStorageItemKeys.ORGANIZATION_TIME_RANGE}
      maxFrom={currentOrganization.create_ts}
    >
      <OrganizationActivityChecker />
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </TimeRangeProvider.Persisted>
  );
});

/**
 * 6. Allow to access workspace routes
 */
export const WorkspaceLayer: FC = withWorkspace(() => {
  useSlackCallback();
  return (
    <>
      <WorkspaceSocket />
      <WorkspaceActivityChecker />
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </>
  );
});
